import React, { useEffect, useState } from 'react';
import { useUserData } from '../contexts/UserDataContext';
import './LandingPage.css';

const API_BASE = '/api';

function getInitials(name: string) {
  return name ? name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase() : '';
}

const LEVELS = [
  { key: '', label: 'All Levels', icon: '🌟' },
  { key: 'beginner', label: 'Beginner', icon: '🌱' },
  { key: 'lower-intermediate', label: 'Lower-Intermediate', icon: '📚' },
  { key: 'intermediate', label: 'Intermediate', icon: '🚀' },
];

const ConversationsPage: React.FC = () => {
  const { userData } = useUserData();
  const selectedLanguage = userData?.selectedLanguage || 'en';
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [conversations, setConversations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [selectedLevel, setSelectedLevel] = useState<string>('');

  useEffect(() => {
    setLoadingCategories(true);
    fetch(`${process.env.REACT_APP_API_URL}/api/conversation-categories?lang=${selectedLanguage}`)
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        console.log('Categories data:', data);
        // Ensure data is an array
        if (Array.isArray(data)) {
          setCategories(data);
        } else {
          console.error('Categories data is not an array:', data);
          setCategories([]);
        }
        setSelectedCategory('');
      })
      .catch(error => {
        console.error('Error fetching categories:', error);
        setCategories([]);
      })
      .finally(() => setLoadingCategories(false));
  }, [selectedLanguage]);

  useEffect(() => {
    if (!selectedCategory) return;
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}/api/conversations?category=${encodeURIComponent(selectedCategory)}&lang=${selectedLanguage}`)
      .then(res => res.json())
      .then(data => setConversations(data))
      .finally(() => setLoading(false));
    setExpandedId(null); // Collapse any open conversation when category changes
  }, [selectedCategory, selectedLanguage]);

  // Filtered categories by search and level
  const filteredCategories = (categories || []).filter((cat: any) =>
    cat.name.toLowerCase().includes(filter.toLowerCase()) &&
    (selectedLevel === '' || cat.level === selectedLevel)
  );

  // Loading skeletons with enhanced styling
  const CategorySkeleton = () => (
    <div className="flex flex-col items-center animate-pulse">
      <div className="w-24 h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full mb-2" />
      <div className="w-32 h-4 bg-gradient-to-r from-gray-100 to-gray-200 rounded" />
    </div>
  );

  const ConversationSkeleton = () => (
    <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 p-6 mb-4 animate-pulse">
      <div className="w-1/2 h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-4" />
      <div className="h-4 w-3/4 bg-gradient-to-r from-gray-100 to-gray-200 rounded mb-2" />
      <div className="h-4 w-2/3 bg-gradient-to-r from-gray-100 to-gray-200 rounded" />
    </div>
  );

  return (
    <div className="landing-bg min-h-screen flex flex-col relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-pink-400/20 to-orange-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-purple-400/10 to-pink-400/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }}></div>
      </div>

      <div className="flex flex-col items-center flex-grow justify-center px-2 md:px-8 py-8 w-full relative z-10">
        {/* Enhanced Header */}
        <div className="text-center mb-8">
          <h1 className="text-6xl font-extrabold tracking-tight drop-shadow-2xl mb-4">
            <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              Real-Life Conversations
            </span>
          </h1>
          <p className="text-xl text-gray-600 font-medium max-w-2xl mx-auto leading-relaxed">
            Practice authentic conversations in your preferred language. Choose your level and start speaking naturally!
          </p>
        </div>

        {/* Enhanced Level Selector */}
        <div className="mb-8 flex flex-wrap gap-4 justify-center w-full max-w-5xl">
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 p-6 w-full">
            <div className="flex flex-wrap gap-3 justify-center">
              {LEVELS.map(level => (
                <button
                  key={level.key}
                  className={`px-6 py-3 rounded-2xl font-semibold text-base transition-all duration-300 transform hover:scale-105 active:scale-95 ${selectedLevel === level.key
                    ? 'bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white shadow-2xl scale-110'
                    : 'bg-white/90 text-gray-700 border border-gray-200 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 hover:border-blue-300 hover:shadow-lg'
                    }`}
                  onClick={() => { setSelectedLevel(level.key); setSelectedCategory(''); }}
                  style={{ minWidth: 140 }}
                >
                  <span className="mr-2">{level.icon}</span>
                  {level.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row w-full max-w-7xl min-h-[600px] gap-6">
          {/* Enhanced Sidebar */}
          <div className="lg:w-1/3 w-full">
            <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 p-6 h-full">
              <div className="mb-6">
                <div className="relative">
                  <input
                    type="text"
                    className="w-full px-6 py-4 rounded-2xl border border-gray-200 focus:outline-none focus:ring-4 focus:ring-blue-400/30 focus:border-blue-400 text-base shadow-sm bg-white/80 backdrop-blur-sm transition-all duration-300"
                    placeholder="🔍 Search categories..."
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                  />
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                    🔍
                  </div>
                </div>
              </div>

              {loadingCategories ? (
                <div className="flex flex-col gap-4">
                  {Array.from({ length: 6 }).map((_, i) => <CategorySkeleton key={i} />)}
                </div>
              ) : filteredCategories.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🔍</div>
                  <div className="text-gray-500 font-semibold text-lg">No categories found.</div>
                  <div className="text-gray-400 text-sm mt-2">Try adjusting your search or level filter.</div>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {filteredCategories.map((cat: any, index: number) => (
                    <button
                      key={cat._id}
                      className={`w-full px-6 py-4 rounded-2xl font-semibold text-left transition-all duration-300 transform hover:scale-105 ${selectedCategory === cat.name
                        ? 'bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white shadow-2xl scale-105'
                        : 'bg-white/80 text-gray-700 border border-gray-200 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 hover:border-blue-300 hover:shadow-lg'
                        }`}
                      onClick={() => setSelectedCategory(cat.name)}
                      style={{
                        animationDelay: `${index * 100}ms`,
                        fontSize: selectedCategory === cat.name ? '1.1rem' : '1rem'
                      }}
                    >
                      <div className="font-bold flex items-center justify-between">
                        <span>{cat.name}</span>
                        <span className="text-xs px-2 py-1 rounded-full bg-white/20">
                          {cat.level || 'All'}
                        </span>
                      </div>
                      {(() => {
                        let desc = cat.description;
                        if (desc && typeof desc === 'object') {
                          desc = desc[selectedLanguage] || desc['en'] || '';
                        }
                        return desc && typeof desc === 'string' ? (
                          <span className="text-sm text-gray-600 mt-2 block opacity-80">{desc}</span>
                        ) : null;
                      })()}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Enhanced Main Area */}
          <div className="lg:w-2/3 w-full">
            <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 p-6 h-full">
              {!selectedCategory ? (
                <div className="text-center py-16">
                  <div className="text-8xl mb-6">💬</div>
                  <div className="text-2xl text-gray-600 font-semibold mb-4">Choose a Category</div>
                  <div className="text-gray-500">Select a conversation category from the sidebar to start practicing.</div>
                </div>
              ) : loading ? (
                <div className="space-y-6">
                  {Array.from({ length: 3 }).map((_, i) => <ConversationSkeleton key={i} />)}
                </div>
              ) : conversations.length === 0 ? (
                <div className="text-center py-16">
                  <div className="text-6xl mb-4">📝</div>
                  <div className="text-xl text-gray-600 font-semibold mb-2">No Conversations Available</div>
                  <div className="text-gray-500">No conversations found for this category.</div>
                </div>
              ) : (
                <div className="space-y-6">
                  {conversations.map((conv: any, index: number) => (
                    <div
                      key={conv._id}
                      className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/30 transition-all duration-500 hover:shadow-3xl hover:scale-[1.02] overflow-hidden"
                      style={{ animationDelay: `${index * 150}ms` }}
                    >
                      <button
                        className={`w-full text-left px-8 py-6 text-xl font-bold transition-all duration-300 ${expandedId === conv._id
                          ? 'bg-gradient-to-r from-blue-100 via-purple-100 to-pink-100 text-blue-800'
                          : 'text-gray-800 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50'
                          }`}
                        onClick={() => setExpandedId(expandedId === conv._id ? null : conv._id)}
                      >
                        <div className="flex items-center justify-between">
                          <span className="flex items-center">
                            <span className="mr-3 text-2xl">💭</span>
                            {conv.title}
                          </span>
                          <span className={`transform transition-transform duration-300 ${expandedId === conv._id ? 'rotate-180' : ''}`}>
                            ▼
                          </span>
                        </div>
                      </button>

                      <div
                        className={`overflow-hidden transition-all duration-500 ease-in-out ${expandedId === conv._id ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                          }`}
                      >
                        {expandedId === conv._id && (
                          <div className="p-8 border-t border-gray-100 space-y-4">
                            {conv.turns.map((turn: any, idx: number) => (
                              <div
                                key={idx}
                                className={`flex flex-col lg:flex-row lg:items-start gap-4 rounded-2xl p-4 transition-all duration-300 ${idx % 2 === 0
                                  ? 'bg-gradient-to-r from-blue-50 to-blue-100/50 border border-blue-200/30'
                                  : 'bg-gradient-to-r from-pink-50 to-pink-100/50 border border-pink-200/30'
                                  }`}
                                style={{ animationDelay: `${idx * 100}ms` }}
                              >
                                {selectedLanguage !== 'en' && (
                                  <div className="flex-1 flex items-start gap-3">
                                    <div className="flex-shrink-0">
                                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${idx % 2 === 0 ? 'bg-pink-200 text-pink-800' : 'bg-blue-200 text-blue-800'
                                        }`}>
                                        {getInitials(turn.speaker)}
                                      </div>
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${idx % 2 === 0 ? 'bg-pink-200 text-pink-700' : 'bg-blue-200 text-blue-700'
                                          }`}>
                                          {turn.speaker}
                                        </span>
                                      </div>
                                      <div className="text-gray-800 font-medium leading-relaxed">
                                        {turn[selectedLanguage] || <span className="italic text-gray-400">(No translation)</span>}
                                      </div>
                                    </div>
                                  </div>
                                )}

                                <div className="flex-1 flex items-start gap-3">
                                  <div className="flex-shrink-0">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${idx % 2 === 0 ? 'bg-blue-200 text-blue-800' : 'bg-pink-200 text-pink-800'
                                      }`}>
                                      {getInitials(turn.speaker)}
                                    </div>
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${idx % 2 === 0 ? 'bg-blue-200 text-blue-700' : 'bg-pink-200 text-pink-700'
                                        }`}>
                                        {turn.speaker}
                                      </span>
                                    </div>
                                    <div className="text-gray-900 font-medium leading-relaxed flex items-center gap-2">
                                      {turn.en}
                                      <button
                                        onClick={() => {
                                          const utterance = new SpeechSynthesisUtterance(turn.en);
                                          utterance.lang = 'en-US';
                                          utterance.rate = 0.9;
                                          speechSynthesis.speak(utterance);
                                        }}
                                        className="flex-shrink-0 p-2 rounded-full bg-blue-500 hover:bg-blue-600 text-white shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-110"
                                        title="Listen to English"
                                      >
                                        <span className="text-xl">🔊</span>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationsPage;

// Enhanced animations
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.innerHTML = `
    @keyframes fadein {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: none; }
    }
    
    @keyframes slidein {
      from { opacity: 0; transform: translateX(-20px); }
      to { opacity: 1; transform: none; }
    }
    
    .animate-fadein {
      animation: fadein 0.6s cubic-bezier(0.4,0.2,0.2,1) both;
    }
    
    .animate-slidein {
      animation: slidein 0.5s cubic-bezier(0.4,0.2,0.2,1) both;
    }
    
    .shadow-3xl {
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    }
    
    .backdrop-blur-sm {
      backdrop-filter: blur(8px);
    }
  `;
  document.head.appendChild(style);
} 